import React, { Fragment, useLayoutEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import CustomAcordion from "../../layout/CustomAcordion";
import HaklaiResults2990Panel from "./form2990/HaklaiResults2990Panel";
import HaklaiNoTaxOutcome from "../HaklaiNoTaxOutcome";
import { HAKLAI_PTOR_TYPES } from "../calc/shared/haklaiConstants";
import { isHaklaiAnalysisFullyExempt } from "../domain/haklaiAnalysisOutcome";
import {
  HAKLAI_ANALYSIS_RESULTS_LABELS,
  HAKLAI_ANALYSIS_RESULTS_MESSAGES
} from "./haklaiAnalysisResultsTexts";
import {
  buildHaklaiPayableTaxDisplayByPropertyKey,
  formatHaklaiWholeMoney
} from "../calc/shared/haklaiMoneyHelpers";

/**
 * Owns the Haklai analysis result screen.
 *
 * Responsibilities:
 * - renders calculation summaries returned from analysis
 * - exposes the generated 2990 form panel
 * - keeps result formatting separate from the editable calculator sections
 */

const PROPERTY_TYPE_LABELS = {
  residential: HAKLAI_ANALYSIS_RESULTS_LABELS.residential,
  commercial: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  commercialBg: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  landAndShared: HAKLAI_ANALYSIS_RESULTS_LABELS.landAndShared,
  farmRemainder: HAKLAI_ANALYSIS_RESULTS_LABELS.farmRemainder
};

const columns = [
  { key: "propertyType", label: HAKLAI_ANALYSIS_RESULTS_LABELS.propertyType, align: "right" },
  { key: "nominalAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.nominalAcquisitionPrice },
  { key: "indexedAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedAcquisitionPrice },
  { key: "saleValue", label: HAKLAI_ANALYSIS_RESULTS_LABELS.saleValue },
  { key: "indexedExpenses", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedExpenses },
  { key: "depreciation", label: HAKLAI_ANALYSIS_RESULTS_LABELS.depreciation },
  { key: "payableTax", label: HAKLAI_ANALYSIS_RESULTS_LABELS.payableTax }
];


function formatCurrency(value) {
  return formatHaklaiWholeMoney(value, { empty: "-" });
}

function formatWholeNumber(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toLocaleString("he-IL", {
    maximumFractionDigits: 0
  });
}

function formatPercent(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  const normalizedPercent = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${formatWholeNumber(normalizedPercent)}%`;
}

function getPropertyDisplay(row) {
  const typeLabel = PROPERTY_TYPE_LABELS[row.propertyType] || row.propertyType || HAKLAI_ANALYSIS_RESULTS_LABELS.genericAsset;
  if (row.propertyLabel && row.propertyLabel === typeLabel) return typeLabel;
  return row.propertyLabel ? `${typeLabel} - ${row.propertyLabel}` : typeLabel;
}

function normalizePtorType(ptorType) {
  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE) return HAKLAI_PTOR_TYPES.SINGLE;
  if (ptorType === HAKLAI_PTOR_TYPES.INHERITANCE) return HAKLAI_PTOR_TYPES.INHERITANCE;
  return HAKLAI_PTOR_TYPES.NONE;
}

function getAnalyzedPtorMeta(row, analyzedPreCalc) {
  const parts = Array.isArray(analyzedPreCalc?.preCalcPartsData)
    ? analyzedPreCalc.preCalcPartsData
    : [];
  const part = parts.find((candidate) => candidate?.haklaiPropertyKey === row?.propertyKey);
  if (!part) {
    return {
      hasPtorMetadata: false,
      ptorType: HAKLAI_PTOR_TYPES.NONE,
      hasTzinunOnPtor: false
    };
  }

  if (Object.prototype.hasOwnProperty.call(part, "haklaiPtorType")) {
    return {
      hasPtorMetadata: true,
      ptorType: normalizePtorType(part.haklaiPtorType),
      hasTzinunOnPtor: part.hasTzinunOnPtor === true
    };
  }

  if (part.isSinglePropertyPtorAllowed === true) {
    return {
      hasPtorMetadata: true,
      ptorType: HAKLAI_PTOR_TYPES.SINGLE,
      hasTzinunOnPtor: part.hasTzinunOnPtor === true
    };
  }

  if (part.isInheritancePtorAllowed === true) {
    return {
      hasPtorMetadata: true,
      ptorType: HAKLAI_PTOR_TYPES.INHERITANCE,
      hasTzinunOnPtor: false
    };
  }

  const hasPtorMetadata = (
    Object.prototype.hasOwnProperty.call(part, "isSinglePropertyPtorAllowed")
    || Object.prototype.hasOwnProperty.call(part, "isInheritancePtorAllowed")
  );
  return {
    hasPtorMetadata,
    ptorType: HAKLAI_PTOR_TYPES.NONE,
    hasTzinunOnPtor: false
  };
}

function getPropertyExemptionLabel(row, { haklaiDraftPreCalc, analyzedPreCalc } = {}) {
  const analyzedPtorMeta = getAnalyzedPtorMeta(row, analyzedPreCalc);
  const hasRowPtorType = Object.prototype.hasOwnProperty.call(row || {}, "ptorType");
  const rowPtorType = normalizePtorType(row?.ptorType);
  const ptorType = hasRowPtorType
    ? rowPtorType
    : analyzedPtorMeta.hasPtorMetadata
      ? analyzedPtorMeta.ptorType
      : row?.propertyKey === "residence_1"
        ? normalizePtorType(haklaiDraftPreCalc?.ptorType)
        : HAKLAI_PTOR_TYPES.NONE;
  const hasTzinunOnPtor = analyzedPtorMeta.hasTzinunOnPtor
    || (
      row?.propertyKey === "residence_1"
      && !analyzedPtorMeta.hasPtorMetadata
      && ptorType === normalizePtorType(haklaiDraftPreCalc?.ptorType)
      && haklaiDraftPreCalc?.hasTzinunOnPtor === true
    );

  if (ptorType === HAKLAI_PTOR_TYPES.NONE) {
    return "";
  }

  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE && hasTzinunOnPtor) {
    return "";
  }

  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE) {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.singleApartmentExemption;
  }

  if (ptorType === HAKLAI_PTOR_TYPES.INHERITANCE) {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.inheritanceExemption;
  }

  return "";
}

function getDataPerPart(mainCalc) {
  return Array.isArray(mainCalc?.calculatedData?.dataPerPart) ? mainCalc.calculatedData.dataPerPart : [];
}

function getMaximalRabinovich(mainCalc) {
  return getDataPerPart(mainCalc).reduce((maxValue, part) => Math.max(maxValue, part.discountRabPercent || 0), 0);
}

export function getHaklaiResultsPrisaData(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (acc, part) => {
      const prisaYears = Array.isArray(part.prisaData)
        ? part.prisaData.reduce((maxYears, personPrisaData) => (
          Math.max(maxYears, Array.isArray(personPrisaData) ? personPrisaData.length : 0)
        ), 0)
        : 0;
      acc.maxPrisaYearsAllowed = Math.max(acc.maxPrisaYearsAllowed, prisaYears);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa47 || 0) - (part.finalLinear47 || 0);
      acc.totalPrisaRev += (part.finalMutavBeforePrisa25 || 0) - (part.finalMutav25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa25 || 0) - (part.finalLinear25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa20 || 0) - (part.finalLinear20 || 0);
      acc.totalBeforePrisa += (part.finalLinearBeforePrisa47 || 0)
        + (part.finalMutavBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa20 || 0)
        + (part.finalInflationLinear10 || 0);
      acc.totalPreAfkaa += (part.finalLinearTaxAllPreAfkaa21 || 0) + (part.finalMutavTaxAllPreAfkaa21 || 0);
      return acc;
    },
    { totalPrisaRev: 0, maxPrisaYearsAllowed: 0, totalBeforePrisa: 0, totalPreAfkaa: 0 }
  );
}

function getAfkaaCredit(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (sum, part) => sum + (part.finalLinearAfkaaZikui22 || 0) + (part.finalMutavAfkaaZikui22 || 0),
    0
  );
}

const HaklaiAnalysisResultsView = ({
  dealId,
  haklaiAnalysis,
  haklaiDraftPreCalc,
  analyzedPreCalc,
  analyzedMainCalc,
  onBackToCalc
}) => {
  const rows = Array.isArray(haklaiAnalysis?.resultsByProperty) ? haklaiAnalysis.resultsByProperty : [];
  const summary = haklaiAnalysis?.summary || {};
  const summaryRaw = haklaiAnalysis?.summaryRaw || {};
  let payableTaxDisplayByPropertyKey = {};
  let hasInconsistentResults = false;
  try {
    payableTaxDisplayByPropertyKey = buildHaklaiPayableTaxDisplayByPropertyKey(
      rows,
      summaryRaw.totalTaxToPay ?? summary.totalTaxToPay ?? 0
    );
  } catch {
    hasInconsistentResults = true;
  }
  const isFullyExemptAnalysis = isHaklaiAnalysisFullyExempt(analyzedPreCalc);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [dealId, haklaiAnalysis?.generatedAt]);

  const hasLossOffsets = Boolean(analyzedMainCalc?.kizuzIski || analyzedMainCalc?.kizuzHoni);
  const hasPrisa = analyzedMainCalc?.prisa?.shouldTryPrisa === true;
  const hasAfkaa = analyzedMainCalc?.afkaa === true;
  const rabinovichDiscountPercent = getMaximalRabinovich(analyzedMainCalc);
  const hasRabinovichDiscount = rabinovichDiscountPercent > 0;
  const prisaData = hasPrisa ? getHaklaiResultsPrisaData(analyzedMainCalc) : null;
  const businessLossOffset = Number(analyzedMainCalc?.kizuzIski || 0);
  const capitalLossOffset = Number(analyzedMainCalc?.kizuzHoni || 0);
  const showCalcDetails = hasLossOffsets || hasPrisa || hasAfkaa || hasRabinovichDiscount;
  const summaryRows = [
    {
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.totalShevach,
      value: formatCurrency(summaryRaw.totalShevach ?? summary.totalShevach)
    },
    {
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.capitalGainsTax,
      value: formatCurrency(
        hasPrisa
          ? prisaData?.totalBeforePrisa
          : summaryRaw.capitalGainsTax ?? summary.capitalGainsTax
      )
    },
    {
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.totalTaxToPay,
      value: formatCurrency(summaryRaw.totalTaxToPay ?? summary.totalTaxToPay),
      isTotal: true
    }
  ];
  const calcDetailRows = [];

  if (businessLossOffset) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.businessLossOffset,
      value: formatCurrency(businessLossOffset)
    });
  }

  if (capitalLossOffset) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.capitalLossOffset,
      value: formatCurrency(capitalLossOffset)
    });
  }

  if (hasRabinovichDiscount) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.rabinovichDiscount,
      value: formatPercent(rabinovichDiscountPercent)
    });
  }

  if (hasPrisa) {
    calcDetailRows.push(
      {
        label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaYears,
        value: formatWholeNumber(prisaData?.maxPrisaYearsAllowed)
      },
      {
        label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaSavings,
        value: formatCurrency(prisaData?.totalPrisaRev)
      },
      {
        label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaTaxAfterPrisa,
        value: formatCurrency(prisaData?.totalPreAfkaa)
      }
    );
  }

  if (hasAfkaa) {
    calcDetailRows.push({
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.afkaaCredit,
      value: formatCurrency(getAfkaaCredit(analyzedMainCalc))
    });
  }
  return (
    <Box className="haklai-results deal-sum-calc">
      <Box className="haklai-results-toolbar">
        <Button className="haklai-results-back" onClick={onBackToCalc}>
          <KeyboardArrowRightIcon />
          <span>{HAKLAI_ANALYSIS_RESULTS_LABELS.backToCalc}</span>
        </Button>
      </Box>

      {hasInconsistentResults ? (
        <Box className="haklai-results-empty">
          <Typography variant="body1">
            {HAKLAI_ANALYSIS_RESULTS_MESSAGES.inconsistentResults}
          </Typography>
        </Box>
      ) : !rows.length ? (
        <Box className="haklai-results-empty">
          <Typography variant="body1">
            {HAKLAI_ANALYSIS_RESULTS_MESSAGES.noResults}
          </Typography>
        </Box>
      ) : (
        <div className="pre-calc-sections haklai-results-sections">
          <CustomAcordion summaryText={HAKLAI_ANALYSIS_RESULTS_LABELS.resultsTitle} defaultExpanded={true}>
            <TableContainer className="haklai-results-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.key === "propertyType" ? "right" : "center"}
                        className={column.key === "propertyType" ? "haklai-results-property-col" : "haklai-results-amount-col"}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const propertyExemptionLabel = getPropertyExemptionLabel(row, {
                      haklaiDraftPreCalc,
                      analyzedPreCalc
                    });
                    return (
                      <TableRow key={row.propertyKey}>
                        <TableCell align="right" className="haklai-results-property-col">
                          <div className="haklai-results-property">
                            <Typography variant="body2" className="haklai-results-property-name">
                              {getPropertyDisplay(row)}
                            </Typography>
                            {propertyExemptionLabel ? (
                              <Typography variant="caption" className="haklai-results-property-meta">
                                {propertyExemptionLabel}
                              </Typography>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" className="haklai-results-amount">
                            {formatCurrency(row.nominalAcquisitionPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" className="haklai-results-amount">
                            {formatCurrency(row.indexedAcquisitionPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" className="haklai-results-amount">
                            {formatCurrency(row.saleValue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" className="haklai-results-amount">
                            {formatCurrency(row.indexedExpenses)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" className="haklai-results-amount">
                            {formatCurrency(row.depreciation)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" className="haklai-results-amount is-total">
                            {formatCurrency(payableTaxDisplayByPropertyKey[row.propertyKey])}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomAcordion>

          {showCalcDetails ? (
            <CustomAcordion summaryText={HAKLAI_ANALYSIS_RESULTS_LABELS.calcDetails} defaultExpanded={true}>
              <section className="haklai-results-metrics">
                {calcDetailRows.map((row) => (
                  <div className="haklai-results-metric" key={row.label}>
                    <span className="haklai-results-metric-label">{row.label}</span>
                    <span className="haklai-results-metric-value">{row.value}</span>
                  </div>
                ))}
              </section>
            </CustomAcordion>
          ) : null}

          <CustomAcordion summaryText={HAKLAI_ANALYSIS_RESULTS_LABELS.summary} defaultExpanded={true}>
            <section className="deal-sum-totals">
              <div className="deal-sum-totals-list">
                {summaryRows.map((row) => (
                  <Fragment key={row.label}>
                    {row.isTotal ? <div className="deal-sum-totals-break" aria-hidden="true" /> : null}
                    <div
                      className={`deal-sum-total-row${row.isTotal ? " is-final" : ""}`}
                      role="group"
                      aria-label={row.label}
                    >
                      <span className="deal-sum-total-label">{row.label}</span>
                      <span className="deal-sum-total-value">{row.value}</span>
                    </div>
                  </Fragment>
                ))}
              </div>
            </section>
          </CustomAcordion>

          {isFullyExemptAnalysis ? (
            <HaklaiNoTaxOutcome />
          ) : (
            <HaklaiResults2990Panel
              dealId={dealId}
              haklaiDraftPreCalc={haklaiDraftPreCalc}
              analyzedPreCalc={analyzedPreCalc}
            />
          )}
        </div>
      )}
    </Box>
  );
};

export default React.memo(HaklaiAnalysisResultsView);
